import type { TFunction } from "i18next";
import { cn, Icon } from "@nexus/ui";
import type { AdminColumn } from "@/features/admin/components/layout";
import { ADMIN_DASH } from "@/features/admin/components/layout/admin-layout-tokens";
import type { SortValue } from "@/features/admin/lib/table-sort";
import { formatCount, formatDuration } from "@/features/admin/lib/format";
import { formatTokensCompact, formatTokensExact, formatTokenShare } from "../lib/format-tokens";
import { CostValue } from "./CostValue";

/** The measures every token breakdown reports, whatever it groups by. */
export interface TokenMeasureRow {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  thinkingTokens: number;
  totalTokens: number;
  costMicros: number;
  avgLatencyMs: number | null;
  failedCalls?: number;
  failureRate?: number;
}

export type TokenMeasureKey =
  | "calls"
  | "inputTokens"
  | "outputTokens"
  | "thinkingTokens"
  | "totalTokens"
  | "costMicros"
  | "avgLatencyMs"
  | "failureRate";

interface TokenMeasureOptions<Row extends TokenMeasureRow> {
  t: TFunction;
  locale: string;
  /** Window total, so each row can show its share of the bill. */
  totalTokens: number;
  /**
   * Tokens this row's cost does not cover. A model row is priced or not; a
   * feature or tenant row can be partly either.
   */
  unpricedTokensOf: (row: Row) => number;
  /**
   * Reported separately from output rather than folded into it: it is billed
   * and invisible in the response. Only the model table can break it out —
   * Claude folds it into `output_tokens`, so a per-tenant column would be a
   * column of dashes for half the platform.
   */
  includeThinking?: boolean;
  /** Fail % of calls in the row. Model and feature tables show it; tenants do not. */
  includeFailure?: boolean;
  /**
   * The spend level a row is marked against, or null when nothing should be
   * marked. A level only means something when the alert is on and evaluates
   * this same window.
   */
  spendAlertTokens?: number | null;
  /** The Fail % a row is marked against (0–100), with the same window rule as spend. */
  failureAlertPercent?: number | null;
}

/**
 * The six (or seven) numeric columns shared by the model, feature and tenant
 * tables.
 *
 * They are built here rather than declared three times so that the three
 * cannot drift on what a share looks like, which title carries the exact
 * figure, or how a row past its alert level is marked. Each table supplies only
 * the column that says what it is grouping by.
 */
export function tokenMeasureColumns<Row extends TokenMeasureRow>({
  t,
  locale,
  totalTokens,
  unpricedTokensOf,
  includeThinking = false,
  includeFailure = false,
  spendAlertTokens = null,
  failureAlertPercent = null,
}: TokenMeasureOptions<Row>): AdminColumn<Row, TokenMeasureKey>[] {
  const isOverAlert = (row: Row) => spendAlertTokens != null && row.totalTokens >= spendAlertTokens;
  const isOverFailureAlert = (row: Row) =>
    failureAlertPercent != null && (row.failureRate ?? 0) * 100 >= failureAlertPercent;

  return [
    {
      id: "calls",
      header: t("admin.llmUsage.table.calls", "Calls"),
      isNumeric: true,
      isSortable: true,
      cell: (row) => formatCount(row.calls, locale),
    },
    ...(includeFailure
      ? [
          {
            id: "failureRate" as const,
            header: t("admin.llmUsage.table.failRate", "Fail %"),
            isNumeric: true,
            isSortable: true,
            cellTitle: (row: Row) =>
              isOverFailureAlert(row)
                ? t(
                    "admin.llmUsage.alerts.failureOverTitle",
                    "Past the {{percent}}% failure alert level for this window",
                    { percent: failureAlertPercent ?? 0 },
                  )
                : undefined,
            cell: (row: Row) => {
              const overAlert = isOverFailureAlert(row);
              return (
                <span className="flex items-center justify-end gap-1">
                  {overAlert ? (
                    <Icon
                      name="alert-circle"
                      size="xs"
                      colorVar="--warning"
                      aria-label={t(
                        "admin.llmUsage.alerts.failureOver",
                        "Past the failure alert level",
                      )}
                    />
                  ) : null}
                  <span className={cn(overAlert ? "text-warning" : "text-muted-foreground")}>
                    {formatFailureRate(row.failureRate)}
                  </span>
                </span>
              );
            },
          },
        ]
      : []),
    {
      id: "inputTokens",
      header: t("admin.llmUsage.table.input", "Input"),
      isNumeric: true,
      isSortable: true,
      cell: (row) => formatTokensCompact(row.inputTokens, locale),
      cellTitle: (row) => formatTokensExact(row.inputTokens, locale),
    },
    {
      id: "outputTokens",
      header: t("admin.llmUsage.table.output", "Output"),
      isNumeric: true,
      isSortable: true,
      cell: (row) => formatTokensCompact(row.outputTokens, locale),
      cellTitle: (row) => formatTokensExact(row.outputTokens, locale),
    },
    ...(includeThinking
      ? [
          {
            id: "thinkingTokens" as const,
            header: t("admin.llmUsage.table.thinking", "Thinking"),
            isNumeric: true,
            isSortable: true,
            // Dashed rather than zeroed for Claude, which folds thinking into
            // output_tokens and so can never report it separately.
            cell: (row: Row) =>
              row.thinkingTokens === 0
                ? ADMIN_DASH
                : formatTokensCompact(row.thinkingTokens, locale),
            cellClassName: "text-muted-foreground",
          },
        ]
      : []),
    {
      id: "totalTokens",
      header: t("admin.llmUsage.table.total", "Total"),
      isNumeric: true,
      isSortable: true,
      cellClassName: "font-medium",
      cellTitle: (row) =>
        isOverAlert(row)
          ? t(
              "admin.llmUsage.alerts.tenantOverTitle",
              "Past the {{tokens}} per-tenant alert level for this window",
              { tokens: formatTokensExact(spendAlertTokens ?? 0, locale) },
            )
          : formatTokensExact(row.totalTokens, locale),
      cell: (row) => {
        const share = formatTokenShare(row.totalTokens, totalTokens, locale);
        const overAlert = isOverAlert(row);
        return (
          <span className="flex items-baseline justify-end gap-1.5">
            {/* The icon, not the colour, is what says "alert" — the same number
                is unreadable to anyone who cannot see the warning hue. */}
            {overAlert ? (
              <Icon
                name="alert-circle"
                size="xs"
                colorVar="--warning"
                aria-label={t(
                  "admin.llmUsage.alerts.tenantOver",
                  "Past the per-tenant alert level",
                )}
              />
            ) : null}
            <span className={cn(overAlert && "text-warning")}>
              {formatTokensCompact(row.totalTokens, locale)}
            </span>
            {share ? (
              <span className="text-[11px] font-normal text-muted-foreground">{share}</span>
            ) : null}
          </span>
        );
      },
    },
    {
      id: "costMicros",
      header: t("admin.llmUsage.table.cost", "Cost"),
      isNumeric: true,
      isSortable: true,
      cell: (row) => (
        <CostValue costMicros={row.costMicros} unpricedTokens={unpricedTokensOf(row)} />
      ),
    },
    {
      id: "avgLatencyMs",
      header: t("admin.llmUsage.table.avgLatency", "Avg"),
      isNumeric: true,
      isSortable: true,
      cell: (row) => formatDuration(row.avgLatencyMs),
      cellClassName: "text-muted-foreground",
    },
  ];
}

/** Every measure sorts on its own raw number; only the label column differs. */
export function tokenMeasureSortValue(row: TokenMeasureRow, key: TokenMeasureKey): SortValue {
  if (key === "failureRate") return row.failureRate ?? 0;
  return row[key] ?? null;
}

function formatFailureRate(rate: number | undefined): string {
  if (rate == null || rate <= 0) return "0%";
  return `${(rate * 100).toFixed(1)}%`;
}
